import Octicon, { X } from '@githubprimer/octicons-react';
import * as React from 'react';
import { Button } from 'reactstrap';
import AutoSuggestContext from './AutoSuggestContext';
import AutoSuggestMenuToggle from './AutoSuggestMenuToggle';
import { toKebabCase } from 'util/StringUtil';
import { useContext } from 'react';
import { useVisuallyHiddenAnnouncement } from '../hooks/useVisuallyHiddenAnnouncement';

export interface AutoSuggestInputActionsProps {
	disabled: boolean | undefined;
	clearable?: boolean;
	name: string;
	focusOnInput?: () => void;
	label?: string;
}

/**
 * Renders the action buttons to clear values and display the autosuggest menu.
 *
 * @function AutoSuggestInputActions
 * @props {React.Component<AutoSuggestInputActionsProps>}
 */
function AutoSuggestInputActions(props: AutoSuggestInputActionsProps) {
	const { disabled = false, clearable = true, name, label, focusOnInput } = props;
	const context = useContext(AutoSuggestContext);
	const { setVisuallyHiddenAnnouncement } = useVisuallyHiddenAnnouncement();

	/**
	 * Clears out value when the clear button is clicked
	 *
	 * @memberof AutoSuggestInput
	 */
	const handleClear = () => {
		const { onChange, downshift } = context;

		onChange!(null);
		downshift?.clearSelection();
		focusOnInput!();
		setVisuallyHiddenAnnouncement(`${label} Field Cleared`);
	};

	const { value, downshift } = context;

	// never show the clear button if we're dealing with an array of values (multiple select)
	const hideClearButton = !value || Array.isArray(value) || !clearable;

	if (!hideClearButton) {
		return (
			<Button
				disabled={disabled}
				onClick={handleClear}
				aria-label="Clear"
				className="btn-input-surrounded btn-input-cancel"
				data-testid={`remove-autosuggest-btn-${toKebabCase(name)}`}
			>
				<Octicon icon={X} ariaLabel="X" />
			</Button>
		);
	}

	return (
		<AutoSuggestMenuToggle
			disabled={disabled}
			isOpen={downshift!.isOpen}
			getToggleButtonProps={downshift!.getToggleButtonProps}
		/>
	);
}

export default AutoSuggestInputActions;
